import React, { useState, useEffect } from 'react';
import { documentsAPI } from '../../../services/api';
import { Button, Card, Modal, Paginator } from '../../../components/ui';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

const DOC_TYPES = ['MoU Draft', 'MoU Signed', 'Proposal', 'Brochure', 'Agreement', 'Invoice', 'Other'];

const fileIcon = (name) => {
  const ext = name?.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼';
  return '📎';
};

const isPdfOrImage = (name) => /\.(pdf|png|jpg|jpeg)$/i.test(name || '');

export default function DocumentsTab({ leadId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: 'Other', status: 'Draft' });
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Clamp page to valid range when docs are added/removed
  useEffect(() => {
    const tp = Math.ceil(docs.length / PAGE_SIZE);
    if (tp > 0 && page > tp) setPage(tp);
  }, [docs.length]);

  const load = async () => {
    try {
      const res = await documentsAPI.getByLead(leadId);
      setDocs(res.data?.data?.documents || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [leadId]);

  const upload = async () => {
    if (!file) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('lead_id', leadId);
      fd.append('name', form.name);
      fd.append('status', form.status);
      await documentsAPI.upload(fd);
      toast.success('Document uploaded');
      setModal(false);
      setFile(null);
      setForm({ name: 'Other', status: 'Draft' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentsAPI.delete(id);
      setDocs(d => d.filter(x => x._id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const viewDoc = async (doc) => {
    // Open window synchronously so popup blockers don't block it
    const win = window.open('', '_blank');
    try {
      const res = await documentsAPI.view(doc._id);
      const url = URL.createObjectURL(res.data);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      win.close();
      toast.error('Could not open file');
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await documentsAPI.download(doc._id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name || doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>Documents</h3>
        <Button size="sm" variant="primary" onClick={() => setModal(true)}>+ Upload</Button>
      </div>

      {loading ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p> : docs.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 24 }}>No documents uploaded yet</p>
      ) : docs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(doc => (
        <Card key={doc._id} style={{ marginBottom: 10, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24 }}>{fileIcon(doc.original_name || doc.name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name || doc.name}</p>
              <p style={{ fontSize: 11, color: '#94a3b8' }}>
                {doc.name} · {doc.status} · {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB · ` : ''}{new Date(doc.createdAt).toLocaleDateString('en-IN')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ padding: '5px 10px', background: '#f1f5f9', color: '#64748b', borderRadius: 6, fontSize: 12 }}>
                v{doc.version || 1}
              </span>
              {isPdfOrImage(doc.original_name || doc.name) && (
                <button onClick={() => viewDoc(doc)} title="View"
                  style={{ padding: '5px 9px', background: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  👁
                </button>
              )}
              <button onClick={() => downloadDoc(doc)} title="Download"
                style={{ padding: '5px 9px', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                ⬇
              </button>
              <button onClick={() => remove(doc._id)} title="Delete"
                style={{ padding: '5px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                ×
              </button>
            </div>
          </div>
        </Card>
      ))}

      <Paginator page={page} totalPages={Math.ceil(docs.length / PAGE_SIZE)} onPageChange={setPage} />

      <Modal open={modal} onClose={() => setModal(false)} title="Upload Document">
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Document Name</label>
              <select value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={sel}>
                {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={sel}>
                {['Draft', 'Sent', 'Under Review', 'Signed', 'Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>File *</label>
            <input type="file" onChange={e => setFile(e.target.files[0])} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ fontSize: 13 }} />
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Max 50 MB. PDF, Word, Excel, images.</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={upload} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
